/**
 * `/configuration` — TUTTA LA CONFIGURAZIONE IN UNA PAGINA, NELLE SUE AREE.
 *
 * ⚠️ **Questa pagina non è una scorciatoia: è l'unico modo di arrivarci.** Nel menu di sinistra
 * «Configurazione» è una voce sola che porta qui — le sue quaranta pagine non sono più
 * righe della colonna. Un gruppo che si apre in quaranta righe non si legge: da chiuso è una
 * riga che non dice niente, da aperto è una colonna più lunga dello schermo in cui si cerca a
 * memoria. Qui invece si vede tutto insieme, nelle aree, e si sceglie guardando.
 *
 * Il gruppo nel menu conserva comunque le sue aree e le sue voci: sono i dati che questa
 * pagina legge, e restano anche il modo in cui ⌘K e le guardie sanno che quelle pagine
 * esistono. La colonna sceglie di non disegnarle (`soloIndice` in `appNav`), e disegna la sola
 * voce che porta qui.
 *
 * ## ⚠️ Le voci arrivano da `appNav.ts`, non sono ricopiate
 *
 * Un elenco di quaranta voci scritto due volte diverge, e questo repo l'ha già pagato tre volte
 * — l'ultima con **tredici** voci di scarto fra colonna desktop e cassetto mobile. Qui si legge
 * lo stesso albero che disegna il menu: una pagina aggiunta là compare qui il giorno stesso, e
 * una tolta sparisce. Comprese le aree: se ne nasce una nuova, questa pagina la mostra senza che
 * nessuno debba ricordarsene.
 *
 * ## Il colore: uno per area, e non vuol dire niente
 *
 * Ogni area prende uno dei **sette colori d'identità** di MaeDesign (`coloreIdentita`), gli
 * stessi del kanban e della rendicontazione. Non portano un significato — non dicono «va bene»
 * o «scaduto», per quello ci sono `warning` e `danger`, e usarli qui per decorare ruberebbe
 * loro la voce. Dicono soltanto «questa è quella di prima»: sette riquadri affiancati si
 * distinguono a colpo d'occhio invece di essere sette rettangoli uguali.
 *
 * ⚠️ Il colore **non è l'unico segno**: c'è anche l'icona dell'area e il titolo. Chi non
 * percepisce quel colore non perde niente, e la tavolozza non promette che due gradini vicini
 * si distinguano fra loro — lo dice `coloreIdentita` — quindi il colore da solo non poteva
 * essere l'informazione.
 *
 * ⚠️ Il gradino si prende dal **titolo** dell'area, non dalla sua posizione: se ne nasce una in
 * mezzo, le altre non cambiano colore sotto gli occhi di chi le aveva imparate.
 *
 * ## ⚠️ E lo stesso gate della colonna
 *
 * `voceVisibile` è la funzione che decide se una voce si vede nel menu, ed è la stessa che
 * decide se la **pagina** si apre (`RouteGate`). Riusarla qui vuol dire che questa panoramica
 * non offre mai una scorciatoia verso un 403: un elenco che mostra tutto a tutti sarebbe un
 * catalogo di porte chiuse.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { Group, SimpleGrid, Text, rem } from '@mantine/core';
import { useState } from 'react';
import { IconAdjustments, IconLayoutGrid, IconLayoutList } from '@tabler/icons-react';
import { MaeShell, MaePageHead, MaeCard, MaeTabs } from '@/shared/desktop/MaeShell';
import { EmptyState } from '@/shared/components/EmptyState';
import { ViewSelector } from '@/shared/components/SelettoreVista';
import { NoticeDot } from '@/shared/components/PuntinoAvviso';
import { useMenuNotices } from '@/features/admin/avvisiMenu';
import { SECTIONS, type NavItem } from '@/app/layouts/appNav';
import { visibleEntry } from '@/app/layouts/navAccess';
import { useAccessContext } from '@/app/layouts/useContestoAccesso';
import { mw, mwSectionLabel } from '@/shared/mobile/tokens';
import { identityByPosition } from '@/shared/theme/coloreIdentita';

type Vista = 'aree' | 'schede';
const VIEWS: readonly Vista[] = ['aree', 'schede'];

export const Route = createFileRoute('/_app/configuration')({
  component: ConfigurationPage,
  /* ⚠️ La vista sta nell'indirizzo, non in uno stato locale: così sopravvive a un
     ricaricamento e si può mandare a un collega già aperta come la si sta guardando. È la
     stessa scelta della scheda azienda (`?section=`). */
  /* ⚠️ `layout` e non `view`: i parametri di ricerca di tutte le rotte finiscono in un tipo
     unico, e `/projects` ha già un `view` che vale `'table'` — dichiararne un altro qui rompeva
     la compilazione di una pagina che non c'entra niente. */
  validateSearch: (s: Record<string, unknown>): { layout?: Vista } => ({
    layout: VIEWS.includes(s.layout as Vista) ? (s.layout as Vista) : undefined,
  }),
});

const GROUP = 'Configurazione';
const THIS_PAGE = '/configuration';

/** La tessera di una voce: l'icona nel quadratino dell'area, il nome, e il link se la pagina c'è. */
function Tessera({ entry, tint, notice }: {
  entry: NavItem;
  tint: { full: string; ink: string };
  /** La segnalazione del server su **questa** pagina, se c'è. */
  notice?: { tone: 'warning' | 'danger'; text: string };
}) {
  const Icon = entry.icon;

  const body = (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        border: `1px solid ${mw.line}`, borderRadius: 12, background: mw.surface,
        padding: '10px 12px', height: '100%',
        /* La voce futura si legge come «non ancora»: smorzata, e il motivo nel `title`. */
        opacity: entry.future ? 0.45 : 1,
        cursor: entry.future ? 'default' : 'pointer',
      }}
      title={entry.future ? `${entry.label}: in arrivo` : undefined}
      aria-disabled={entry.future}
    >
      {/*
        ⚠️ L'inchiostro dell'icona è `--on-cat-N`, **calcolato** per stare sopra quel gradino, non
        un bianco fisso: `--cat-2` è chiaro e ci vuole inchiostro scuro, e un bianco su quello
        cadrebbe a 1,4:1 — un'icona che non si vede.
      */}
      <span
        style={{
          width: rem(28), height: rem(28), borderRadius: 8, flexShrink: 0,
          background: tint.full, color: tint.ink,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon size={16} stroke={1.9} />
      </span>
      <Text
        style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900, minWidth: 0, flex: 1 }}
        lineClamp={1}
      >
        {entry.label}
      </Text>
      {/*
        ⚠️ Il puntino sta **qui**, sulla pagina che ha il problema — non solo sulla riga di menu.
        Nella colonna «Configurazione» dice soltanto che dentro c'è qualcosa; è aprendo che si
        deve vedere **cosa**, o si entra e si ricomincia a cercare a memoria, che è il difetto
        da cui questa pagina nasce.
      */}
      {notice && <NoticeDot tone={notice.tone} text={notice.text} />}
    </div>
  );

  if (entry.future) return body;
  return <Link to={entry.to} style={{ textDecoration: 'none' }}>{body}</Link>;
}

function ConfigurationPage() {
  const { layout } = Route.useSearch();
  const navigate = Route.useNavigate();
  const vista: Vista = layout ?? 'aree';
  const access = useAccessContext();
  const group = SECTIONS.find((s) => s.title === GROUP);

  /*
   * Le aree con dentro solo ciò che questa persona può aprire; un'area che resta vuota non
   * stampa nemmeno il titolo, o sarebbe un'intestazione su niente.
   *
   * ⚠️ E **senza questa pagina**: la voce «Panoramica amministrazione» punta esattamente qui, e
   * un elenco che offre di andare dove si è già è una riga che non fa niente. Si toglie
   * confrontando l'indirizzo, non il nome: così resta tolta anche se un domani la voce si
   * chiama in un altro modo, e non c'è niente da ricordarsi. Nel menu invece serve — è da lì
   * che ci si arriva.
   */
  /* Le segnalazioni del server sulle pagine di qui: la stessa sorgente dei puntini del menu. */
  const notices = useMenuNotices(
    (group?.groups ?? []).flatMap((g) => g.items.map((v) => v.to)),
  );

  const areas = (group?.groups ?? [])
    .map((g) => ({
      titolo: g.title,
      icon: g.icon,
      entries: g.items.filter((v) => v.to !== THIS_PAGE && visibleEntry(v, access)),
    }))
    .filter((a) => a.entries.length > 0);

  /* La scheda aperta: la prima che questa persona vede, se quella nell'indirizzo non c'è più
     (un'area rinominata, o un permesso che è cambiato da quando il link è stato copiato). */
  const [detail, setDetail] = useState<string | null>(null);
  const validOpen = detail && areas.some((a) => a.titolo === detail) ? detail : (areas[0]?.titolo ?? null);

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconAdjustments size={22} />}
        title="Configurazione"
        /* ⚠️ Dice **cosa stai guardando**, non quante sono né come sono divise: il conteggio
           lo si vede, la divisione anche, e una frase che descrive la pagina invece di
           spiegarla non aiuta chi ci arriva senza sapere dov'è capitato. */
        sub="Le impostazioni, i cataloghi e le regole che valgono per tutta l’installazione — non per una singola azienda o persona."
        tone="bordeaux"
        actions={(
          <ViewSelector
            views={[
              { value: 'aree', label: 'Aree', icon: <IconLayoutList size={15} /> },
              { value: 'schede', label: 'Schede', icon: <IconLayoutGrid size={15} /> },
            ]}
            active={vista}
            onChange={(v) => void navigate({
              /* La vista predefinita non si scrive nell'indirizzo: un link pulito è quello che
                 la gente incolla, e `?layout=aree` sarebbe rumore che dice il valore di
                 partenza. */
              search: { layout: v.value === 'aree' ? undefined : (v.value as Vista) },
              replace: true,
            })}
            groupLabel="Come guardare la configurazione"
          />
        )}
      />

      {areas.length === 0 ? (
        /* ⚠️ Non una pagina bianca: chi arriva qui senza nessun permesso di amministrazione
           deve capire che è una questione di autorizzazioni, non un guasto. */
        <EmptyState
          icon={IconAdjustments}
          title="Nessuna pagina di configurazione ti è aperta"
          description="Le configurazioni del prodotto sono governate da permessi: se ti serve una di queste pagine, chiedila a chi amministra l’installazione."
        />
      ) : vista === 'schede' ? (
        /*
         * LA VISTA A SCHEDE: le aree diventano schede, e si guarda un'area per volta.
         *
         * ⚠️ Il colore resta quello dell'area — lo stesso gradino che la stessa area ha
         * nell'altra vista, perché è la posizione a deciderlo e non la scheda aperta. Se
         * cambiasse passando da una vista all'altra, il colore smetterebbe di essere il modo
         * per riconoscere l'area e diventerebbe decorazione.
         */
        <>
          <MaeTabs
            value={validOpen ?? ''}
            onChange={(v) => setDetail(v)}
            tabs={areas.map((a) => ({
              value: a.titolo,
              label: a.titolo,
              count: a.entries.length,
            }))}
            mb={14}
          />
          {areas.filter((a) => a.titolo === validOpen).map((area) => {
            const tinta = identityByPosition(areas.indexOf(area));
            return (
              <MaeCard key={area.titolo}>
                <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 4 }} spacing={10}>
                  {area.entries.map((v) => (
                    <Tessera key={v.to} entry={v} tint={tinta} notice={notices.get(v.to)} />
                  ))}
                </SimpleGrid>
              </MaeCard>
            );
          })}
        </>
      ) : (
        areas.map((area, i) => {
          /*
           * ⚠️ Il gradino si prende dalla **posizione**, non dal titolo.
           *
           * Con l'hash di `coloreIdentita` le otto aree ne prendevano **quattro**: tre
           * bordeaux, tre azzurre, e due delle tre bordeaux erano adiacenti — «Assistenza &
           * legali» sopra «Sistema & log». Con il colore addosso a quarantun tessere quella
           * collisione non è un difetto estetico: è il colore che dice «stessa area» dove
           * l'area è un'altra.
           *
           * Il prezzo, dichiarato in `identitaPerPosizione`: inserire un'area in mezzo sposta i
           * colori di quelle sotto. Lo fa una persona di proposito, e se ne accorge subito.
           */
          const tinta = identityByPosition(i);
          const AreaIcon = area.icon;
          return (
            <MaeCard key={area.titolo} style={{ marginBottom: 14 }}>
              {/*
                ⚠️ La testata dell'area è **neutra**, e il colore sta tutto sulle tessere.
                Ripeterlo anche qui — filetto, icona e titolo tinti — vorrebbe dire dire la
                stessa cosa quattro volte nello stesso riquadro: il colore perde forza proprio
                dove serve, cioè in mezzo alle quarantun tessere.
              */}
              <Group justify="space-between" align="center" mb={10} wrap="nowrap">
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                  {AreaIcon && <AreaIcon size={16} stroke={1.9} style={{ color: mw.ink300, flexShrink: 0 }} />}
                  <Text style={{ ...mwSectionLabel, marginBottom: 0 }}>{area.titolo}</Text>
                </Group>
                {/* Quante pagine ci sono dentro: è la cosa che il menu non dice mai, né da
                    chiuso né da aperto. `tabular-nums` perché i numeri in colonna si leggano
                    incolonnati. */}
                <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, color: mw.ink300, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {area.entries.length}
                </Text>
              </Group>
              <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 4 }} spacing={10}>
                {area.entries.map((v) => (
                  <Tessera key={v.to} entry={v} tint={tinta} notice={notices.get(v.to)} />
                ))}
              </SimpleGrid>
            </MaeCard>
          );
        })
      )}
    </MaeShell>
  );
}
